<template>
  <div class="">
    <div>
      <div class="text-body2 text-bold q-px-sm">
        Total Market Depth of {{ props.symbol }}
      </div>
      <VueApexCharts
        type="area"
        :height="chartHeight || 350"
        :options="chartOptions"
        :series="seriesTotal"
      />
    </div>

    <q-separator class="q-my-sm" />

    <div>
      <div class="text-body2 text-bold q-px-sm">
        Top 5 Market Depth of {{ props.symbol }}
      </div>
      <VueApexCharts
        type="area"
        :height="chartHeight || 350"
        :options="chartOptions"
        :series="series"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useQuasar } from "quasar";
import useDataV2 from "src/use/datav2";

import VueApexCharts from "vue3-apexcharts";

const props = defineProps({
  symbol: { type: String, required: true },
  chartHeight: { type: Number, default: null },
});

const $q = useQuasar();

const { marketDepth, marketDepthTotal } = useDataV2();

const marketDepthData = ref([]);
const marketDepthTotalData = ref([]);

const loadData = async (symbol) => {
  try {
    const [resMarketDepth, resMarketDepthTotal] = await Promise.all([
      marketDepth(symbol),
      marketDepthTotal(symbol),
    ]);

    marketDepthData.value = resMarketDepth.data;
    marketDepthTotalData.value = resMarketDepthTotal.data;
  } catch (error) {
    console.error("Error loading market depth data:", error);
  }
};

const series = computed(() => {
  const buyData = [];
  const sellData = [];

  marketDepthData.value.forEach(
    ({ created_at, timestamp, buy_qty, sell_qty }) => {
      const utcDate = new Date(
        created_at.replace(" ", "T") + "Z"
      ).toISOString();

      buyData.push([utcDate, buy_qty]);
      sellData.push([utcDate, sell_qty]);
    }
  );

  return [
    { name: "Buy Qty", data: buyData },
    { name: "Sell Qty", data: sellData },
  ];
});

const seriesTotal = computed(() => {
  const buyData = [];
  const sellData = [];

  marketDepthTotalData.value.forEach(
    ({ created_at, timestamp, buy_qty_total, sell_qty_total }) => {
      const utcDate = new Date(
        created_at.replace(" ", "T") + "Z"
      ).toISOString();

      buyData.push([utcDate, buy_qty_total]);
      sellData.push([utcDate, sell_qty_total]);
    }
  );

  return [
    { name: "Buy Qty", data: buyData },
    { name: "Sell Qty", data: sellData },
  ];
});

const chartOptions = computed(() => {
  const isDarkMode = $q.dark.mode;
  const textColor = isDarkMode ? "#fff" : "#000";

  return {
    chart: {
      type: "area",
      height: props.chartHeight,
      zoom: { enabled: false, type: "x", autoScaleYaxis: true },
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },

    xaxis: {
      datetimeUTC: false,
      tickPlacement: "between",
      type: "datetime",
      tickAmount: 7,
      tickPlacement: "between",
      labels: { rotate: 0, style: { colors: textColor } },
    },
    yaxis: {
      opposite: true,
      title: { text: "Quantity", style: { color: textColor } },
      labels: {
        formatter: function (val) {
          return parseInt(val);
        },
        style: { colors: textColor },
      },
    },
    tooltip: {
      theme: isDarkMode ? "dark" : "light",
      x: { format: "dd MMM yyyy HH:mm" },
    },
    legend: {
      labels: {
        colors: textColor,
        useSeriesColors: false,
      },
    },
    colors: ["#15996d", "#ff3366"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
  };
});

watch(
  () => props.symbol,
  (newSymbol) => {
    if (newSymbol) loadData(newSymbol);
  },
  { immediate: true }
);
</script>
